// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    CognitoAccessToken,
    CognitoIdToken,
    CognitoRefreshToken,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool,
    CognitoUserSession,
    ICognitoUserSessionData,
    NodeCallback,
} from 'amazon-cognito-identity-js';
import { BehaviorSubject, catchError, from, map, Observable, of, skip, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorService } from '@services/error.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrorModalComponent } from 'src/app/modals/error-modal/error-modal.component';
import { RequestService } from '@services/request.service';
import { AuthenticationResponse as AuthenticationResponse, AuthenticationStatus } from '@interfaces/auth.interfaces';
import { RpcApiError, RpcApiResponse } from '@interfaces/api.interfaces';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _isAuthenticated$ = new BehaviorSubject(false);
    private _cognitoUser: CognitoUser;
    private _userPool = new CognitoUserPool(environment.cognito);
    public redirectUrl: string = null;
    public logoutTimeout;
    private _authEmailAddress: string;
    private _authChallengeName: string;
    private _authSessionId: string;

    get token(): string {
        return this._cognitoUser?.getSignInUserSession()?.getAccessToken()?.getJwtToken();
    }

    get sessionExpiration(): number {
        return this._cognitoUser?.getSignInUserSession()?.getAccessToken()?.getExpiration();
    }

    constructor(
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly errorService: ErrorService,
        private readonly requestService: RequestService
    ) {
        this._isAuthenticated$.pipe(skip(1)).subscribe((isAuthenticated) => {
            // do not redirect to login for the direct logon flow
            if (
                !isAuthenticated &&
                !this.router.url.startsWith('/login/firstUserLogging/entrypoint') &&
                !this.router.url.startsWith('/login/request-access/')
            ) {
                this.goToLogin();
            }
        });
    }

    sessionExpirationOccurred(): boolean {
        return this.sessionExpiration && this.sessionExpiration <= Math.floor(Date.now() / 1000);
    }

    startLogoutTimer() {
        this.cancelLogoutTimeout();
        let timeRemaining;

        if (this.sessionExpiration) {
            const currentTimestamp = Math.floor(Date.now() / 1000);
            timeRemaining = this.sessionExpiration - currentTimestamp;
        } else {
            timeRemaining = this.getDefaultLogoutTime();
        }

        this.logoutTimeout = setTimeout(() => {
            if (this.sessionExpirationOccurred()) {
                this.signOut();
                this.goToLogin();
            } else {
                this.cancelLogoutTimeout();
                this.startLogoutTimer();
            }
        }, timeRemaining * 1000);
    }

    getDefaultLogoutTime() {
        //we are using 16 hours as default session expiration
        const currentTimestamp = Math.floor(Date.now() / 1000);
        return currentTimestamp + 57600;
    }

    cancelLogoutTimeout() {
        // Cancel the timeout if it's still active
        clearTimeout(this.logoutTimeout);
    }

    authenticateUser(
        email: string,
        password: string,
        successCallback: (status: AuthenticationResponse) => void,
        failureCallback: () => void,
        mfaCallback: (status: AuthenticationStatus) => void,
        newPasswordRequiredCallback: () => void = undefined,
        suppressError = false
    ): void {
        this._authEmailAddress = email;
        this.requestService
            .post$<AuthenticationResponse>('api/auth/init', {
                email,
                password,
            })
            .pipe(
                catchError((err) => {
                    this._isAuthenticated$.next(false);
                    if (!suppressError) {
                        this.errorService.showError({
                            statusCode: 500,
                            message: this.determineAuthErrorMessage(err),
                            title: 'Login Attempt Unsuccessful',
                        });
                    }
                    failureCallback();
                    return of(null);
                })
            )
            .subscribe((state?: RpcApiResponse<AuthenticationResponse>) => {
                this.dispatchAuthenticationResponse(state.data, successCallback, mfaCallback, newPasswordRequiredCallback);
            });
    }

    private dispatchAuthenticationResponse(
        response: AuthenticationStatus,
        successCallback: (_: AuthenticationStatus) => void,
        mfaCallback: (status: AuthenticationStatus) => void,
        newPasswordRequiredCallback: () => void = undefined
    ) {
        if (response?.accessToken?.length > 0) {
            // login success
            this.setSessionTokens(response.logonId, response.accessToken, response.idToken, response.refreshToken);
            successCallback(response);
        } else if (response?.challengeName?.length > 0) {
            this._authChallengeName = response.challengeName;
            this._authSessionId = response.sessionId;
            if ('SMS_MFA' === response?.challengeName) {
                // an mfa challenge needs to be responded to
                this.startLogoutTimer();
                mfaCallback(response);
            } else {
                // new password required
                this.startLogoutTimer();
                // redirect to new password form, unless the caller wants to do something different
                if (!newPasswordRequiredCallback) {
                    this.router.navigate(['/login', 'new-password']);
                } else {
                    newPasswordRequiredCallback();
                }
            }
        }
    }

    sendMFA(mfaCode: string, successCallback: (_: AuthenticationStatus) => void, failureCallback: () => void) {
        this.requestService
            .post$<AuthenticationStatus>('api/auth/continue', {
                email: this._authEmailAddress,
                sessionId: this._authSessionId,
                challengeName: this._authChallengeName,
                challengeResponse: mfaCode,
            })
            .pipe(
                catchError((err) => {
                    this._isAuthenticated$.next(false);
                    const error: string = err?.error?.message?.toLowerCase() || '';
                    if (error.indexOf('invalid code') !== -1) {
                        this.dialog.open(ErrorModalComponent, {
                            width: '700px',
                            data: {
                                title: 'Invalid Verification Code',
                                message:
                                    'The verification code you entered is invalid. Please close this window, check the number and try again. If you need a new code, click on Request New Code to start over.',
                            },
                        });
                    } else if (error.indexOf('session is expired') !== -1) {
                        this.dialog.open(ErrorModalComponent, {
                            width: '700px',
                            data: {
                                title: 'Verification Code Has Expired',
                                message:
                                    'The verification code is only valid for 5 minutes. Please close this window and click on Request New Code to try again.',
                            },
                        });
                    } else {
                        this.errorService.showError('Unable to authenticate with provided token.');
                    }

                    failureCallback();
                    return of(null);
                })
            )
            .subscribe((state: RpcApiResponse<AuthenticationStatus>) => {
                if (state) {
                    // login success
                    const session = this.setSessionTokens(state?.data?.logonId, state.data.accessToken, state.data.idToken, state.data.refreshToken);
                    successCallback(state.data);
                }
            });
    }

    checkAuthState(): void {
        //get the current user. checks if authenticated and gets session/refresh token
        this._cognitoUser = this._userPool.getCurrentUser();

        this.isAuthenticated().subscribe((response) => {
            if (!response || !this._cognitoUser) {
                this._isAuthenticated$.next(false);
                this._cognitoUser = undefined;
                return;
            }

            if (response) {
                this._isAuthenticated$.next(true);
            }
        });
    }

    setRedirectUrl(url: string): Observable<void> {
        return this.isAuthenticated$().pipe(
            take(1),
            map((authenticated) => {
                if (!authenticated) {
                    this.redirectUrl = url;
                }
            })
        );
    }

    completeNewPasswordChallenge(
        password: string,
        successCallback: (_: AuthenticationStatus) => void,
        failureCallback: () => void,
        mfaCallback: (_: AuthenticationStatus) => void
    ): void {
        this.requestService
            .post$<AuthenticationStatus>('api/auth/continue', {
                email: this._authEmailAddress,
                sessionId: this._authSessionId,
                challengeName: this._authChallengeName,
                challengeResponse: password,
            })
            .pipe(
                catchError((err) => {
                    failureCallback();
                    this.errorService.showError({
                        title: 'Action Could Not be Completed',
                        message: err.message,
                    });
                    return of(null);
                })
            )
            .subscribe((status) => {
                this.dispatchAuthenticationResponse(status.data, successCallback, mfaCallback);
            });
    }

    confirmResetPassword(email: string, token: string, password: string, onSuccess: () => void, onFailure: () => void): void {
        this.requestService
            .post$<void>('api/user/resetuser/confirm', {
                email: email,
                resetToken: token,
                newPassword: password,
            })
            .pipe(
                catchError((err: RpcApiResponse<RpcApiError>) => {
                    onFailure();

                    const error = err?.error?.name || '';
                    const errorMessage = err?.error?.message || '';

                    let title = 'Action Could Not be Completed';
                    let message = errorMessage;
                    if (-1 !== error.toLowerCase().indexOf('codemismatch')) {
                        title = 'Invalid Verification Code';
                        message =
                            'The verification code you entered is invalid. Please close this window, check the number and try again. If you need a new code, click on Request New Code to start over.';
                    } else if (-1 !== error.toLowerCase().indexOf('expiredcode')) {
                        title = 'Password Reset Timeout';
                        message =
                            'The system requires you to update your password within 60 minutes. If you received this error when resetting your password, please close this window, click the Request New Reset Token link and follow the instructions to begin the password reset process again.';
                    }

                    this.errorService.showError({
                        title,
                        message,
                    });
                    throw err;
                })
            )
            .subscribe(() => {
                onSuccess();
            });
    }

    changePassword(oldPassword: string, newPassword: string, callback: NodeCallback<Error, 'SUCCESS'>): void {
        return this._cognitoUser.changePassword(oldPassword, newPassword, callback);
    }

    signInUser(cognitoSession: CognitoUserSession): void {
        this._cognitoUser.setSignInUserSession(cognitoSession);
        this._isAuthenticated$.next(true);
        this.startLogoutTimer();
    }

    forgotPasswordInit(logonId: string): void {
        this.setCognitoUser(logonId);
        this._cognitoUser.forgotPassword({
            onSuccess: () => {
                // successfully initiated reset password request
            },
            onFailure: (err) => {
                this.errorService.showError(err.message ? err.message : { statusCode: 500, message: err.message });
            },
            //Optional automatic callback
            inputVerificationCode: () => {
                this.router.navigate(['/login', 'reset-token']).then(() => {
                    this.setCognitoUser(logonId);
                });
            },
        });
    }

    goToLogin(): void {
        if (window.location.pathname.indexOf('/pddj') === 0) {
            this.router.navigate(['/pddj', 'login']);
        } else {
            this.router.navigate(['/login']);
        }
    }

    isAuthenticated$(): Observable<boolean> {
        return this._isAuthenticated$.asObservable();
    }

    isAuthenticated(): Observable<boolean> {
        const cUser = this._userPool.getCurrentUser();

        return from<Promise<boolean>>(
            new Promise<boolean>((resolve, reject) => {
                if (!cUser) {
                    this.dialog.closeAll();
                    this.signOut();
                    return resolve(false);
                }

                if (!this._cognitoUser || this._cognitoUser === undefined) {
                    this.dialog.closeAll();
                    this.signOut();
                    return resolve(false);
                }

                //get the session so we can then check for a refresh token
                this._cognitoUser?.getSession((err, session) => {
                    if (err) {
                        /*
                        code: "NotAuthorizedException"
                        message: "Refresh Token has expired"
                        name: "NotAuthorizedException"
                        */
                        this.dialog.closeAll();
                        this.signOut();
                        return resolve(false);
                    }
                    this._cognitoUser.setSignInUserSession(session);
                });

                if (this._cognitoUser?.getSignInUserSession()) {
                    this._cognitoUser?.refreshSession(this._cognitoUser?.getSignInUserSession()?.getRefreshToken(), (err, session) => {
                        //session is not valid. Close any modals and sign the user out
                        if (err) {
                            this.dialog.closeAll();
                            this.signOut();
                            return resolve(false);
                        } else {
                            this._cognitoUser.setSignInUserSession(session);
                            const isAuth = session.isValid();
                            return resolve(isAuth);
                        }
                    });
                } else {
                    //session must not be valid for some reason. Close any modals and sign the user out
                    this.dialog.closeAll();
                    this.signOut();
                    return resolve(false);
                }
            })
        );
    }

    registerUser(params: { email: string; password: string }): void {
        const dataEmail = {
            Name: 'email',
            Value: params.email,
        };
        const attributeEmail = new CognitoUserAttribute(dataEmail);
        this._userPool.signUp(
            params.email, //'username',
            params.password, //'password',
            [attributeEmail],
            null,
            (error, result) => {
                if (error) {
                    this.errorService.showError(
                        error.message
                            ? error.message
                            : {
                                  statusCode: 500,
                                  message: error.message,
                              }
                    );
                    return;
                }
                this._cognitoUser = result.user;
                this.router.navigate(['/login', 'reset-password']);
            }
        );
    }

    setCognitoUser(userName: string): void {
        //username here should be logonId and not email
        this._cognitoUser = new CognitoUser({
            Username: userName,
            Pool: this._userPool,
        });
    }

    setSessionTokens(logonId: string, AccessToken: string, IdToken: string, RefreshToken?: string): CognitoUserSession {
        const tokens: ICognitoUserSessionData = {
            AccessToken: new CognitoAccessToken({ AccessToken }),
            IdToken: new CognitoIdToken({ IdToken }),
            RefreshToken: new CognitoRefreshToken({ RefreshToken }),
        };
        this.setCognitoUser(logonId);
        const session = new CognitoUserSession(tokens);
        this.signInUser(session);
        this._isAuthenticated$.next(true);
        return session;
    }

    signOut(): void {
        this._cognitoUser?.signOut();
        this._isAuthenticated$.next(false);
    }

    determineAuthErrorMessage(error: any): string {
        let message = '';
        switch (error.message) {
            case 'User is disabled.':
                message =
                    'Your access to the application has been suspended, either temporarily or permanently. Contact a system administrator at the dealership to re-establish it.';
                break;
            case 'Temporary password has expired and must be reset by an administrator.':
                message = 'The link you received in your welcome message has expired. Please close this window and click on Reset Password to start again.';
                break;
            default:
                message =
                    'Please check your email and / or password to ensure you have entered them correctly. If you have forgotten your password, close this window and click on Reset Password to create a new one. If you cannot remember your email address, contact the dealership.';
                break;
        }
        return message;
    }
}
